/**
 * Admin Metrics Service
 * Provides analytics and usage metrics for admin dashboard
 * Supports multiple admin users stored in Firestore
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  Timestamp,
} from 'firebase/firestore';
import { ref, listAll, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { ProfileService } from './profileService';
import type { FamilyMember } from '../types/family-sharing';

// Collection names
const PROFILES_COLLECTION = 'profiles';
const PILOTS_COLLECTION = 'pilots';
const HEALTH_PROVIDERS_COLLECTION = 'healthProviders';
const FINANCIAL_INSTITUTIONS_COLLECTION = 'financialInstitutions';
const INSURANCE_POLICIES_COLLECTION = 'insurancePolicies';
const REMINDERS_COLLECTION = 'reminders';
const ADMIN_CONFIG_COLLECTION = 'adminConfig';
const ADMIN_CONFIG_DOC = 'settings';
const ADMIN_AUDIT_COLLECTION = 'adminAuditLogs';

// Primary admin email - always has access (cannot be removed)
export const ADMIN_EMAIL = 'prabhu2babu@gmail.com';

export interface AdminUser {
  email: string;
  addedAt: string;
  addedBy: string;
}

export type MembershipPlan = 'free' | 'pro' | 'premium';

export type AccountStatus = 'active' | 'disabled';

export interface UserMetrics {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  createdAt?: string;
  lastSignIn?: string;
  membershipPlan: MembershipPlan;
  accountStatus: AccountStatus;
  disabledAt?: string;
  disabledBy?: string;
  disabledReason?: string;
  profileCount: number;
  totalAddresses: number;
  totalVehicles: number;
  totalMembers: number;
  totalReminders: number;
  totalHealthProviders: number;
  totalFinancialInstitutions: number;
  totalInsurancePolicies: number;
  // Role counts across all profiles
  rolesPilot?: number;      // Profiles where user is owner
  rolesCoPilot?: number;    // Co-Pilots in user's own profiles
  rolesPassenger?: number;  // Passengers in user's own profiles
}

export interface AdminAuditLog {
  id: string;
  action: 'switch_membership' | 'disable_account' | 'enable_account' | 'reset_trial' | 'hard_delete_user';
  targetUserId: string;
  targetUserEmail: string;
  adminEmail: string;
  oldValue?: string;
  newValue?: string;
  note?: string;
  timestamp: string;
}

export interface OverallMetrics {
  totalUsers: number;
  totalProfiles: number;
  totalAddresses: number;
  totalVehicles: number;
  totalMembers: number;
  totalReminders: number;
  totalHealthProviders: number;
  totalFinancialInstitutions: number;
  totalInsurancePolicies: number;
  usersThisWeek: number;
  usersThisMonth: number;
  activeUsersLast7Days: number;
  avgAddressesPerUser: number;
  avgVehiclesPerUser: number;
  avgRemindersPerUser: number;
  // Engagement metrics
  usersWithFirstItemIn24h: number;
  usersWithFirstItemIn24hPercent: number;
  usersReturnedAfter7Days: number;
  usersReturnedAfter7DaysPercent: number;
  usersWithAtLeastOneReminder: number;
  usersWithAtLeastOneReminderPercent: number;
  shareLinkSignups: number;
  shareLinkConversionPercent: number;
  // Note: reminderTriggeredSessions would require additional tracking
}

export interface SignupTrend {
  date: string;
  count: number;
}

export interface CategoryBreakdown {
  category: string;
  count: number;
  percentage: number;
}

class AdminMetricsService {
  // Cache for admin emails to avoid repeated Firestore calls
  private adminEmailsCache: string[] | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_TTL = 60000; // 1 minute cache

  /**
   * Check if user is the primary admin
   */
  isPrimaryAdmin(email: string | null | undefined): boolean {
    return email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();
  }

  /**
   * Check if user is authorized admin (primary or additional)
   */
  async isAuthorizedAdminAsync(email: string | null | undefined): Promise<boolean> {
    if (!email) return false;
    const normalizedEmail = email.toLowerCase();

    // Primary admin always has access
    if (normalizedEmail === ADMIN_EMAIL.toLowerCase()) {
      return true;
    }

    // Check additional admins
    const adminEmails = await this.getAdminEmails();
    return adminEmails.some(e => e.toLowerCase() === normalizedEmail);
  }

  /**
   * Synchronous check - uses cache, falls back to primary admin only
   */
  isAuthorizedAdmin(email: string | null | undefined): boolean {
    if (!email) return false;
    const normalizedEmail = email.toLowerCase();

    // Primary admin always has access
    if (normalizedEmail === ADMIN_EMAIL.toLowerCase()) {
      return true;
    }

    // Check cache if valid
    if (this.adminEmailsCache && Date.now() - this.cacheTimestamp < this.CACHE_TTL) {
      return this.adminEmailsCache.some(e => e.toLowerCase() === normalizedEmail);
    }

    // If no valid cache, only primary admin has sync access
    // (async check will be done separately)
    return false;
  }

  /**
   * Get all admin emails from Firestore
   */
  async getAdminEmails(): Promise<string[]> {
    // Return cache if valid
    if (this.adminEmailsCache && Date.now() - this.cacheTimestamp < this.CACHE_TTL) {
      return this.adminEmailsCache;
    }

    try {
      const configRef = doc(db, ADMIN_CONFIG_COLLECTION, ADMIN_CONFIG_DOC);
      const configSnap = await getDoc(configRef);

      if (configSnap.exists()) {
        const data = configSnap.data();
        const additionalAdmins: AdminUser[] = data.additionalAdmins || [];
        const emails = [ADMIN_EMAIL, ...additionalAdmins.map(a => a.email)];

        // Update cache
        this.adminEmailsCache = emails;
        this.cacheTimestamp = Date.now();

        return emails;
      }

      // No config document yet, return only primary admin
      this.adminEmailsCache = [ADMIN_EMAIL];
      this.cacheTimestamp = Date.now();
      return [ADMIN_EMAIL];
    } catch {
      // Silently return primary admin only for permission errors
      // This is expected - only admins can read the admin config
      return [ADMIN_EMAIL];
    }
  }

  /**
   * Get all admin users with details
   */
  async getAdminUsers(): Promise<AdminUser[]> {
    try {
      const configRef = doc(db, ADMIN_CONFIG_COLLECTION, ADMIN_CONFIG_DOC);
      const configSnap = await getDoc(configRef);

      if (configSnap.exists()) {
        const data = configSnap.data();
        return data.additionalAdmins || [];
      }

      return [];
    } catch {
      // Silently return empty for permission errors
      // This is expected - only admins can read the admin config
      return [];
    }
  }

  /**
   * Add a new admin user (only primary admin can do this)
   */
  async addAdminUser(email: string, addedByEmail: string): Promise<void> {
    if (!this.isPrimaryAdmin(addedByEmail)) {
      throw new Error('Only the primary admin can add new admins');
    }

    const normalizedEmail = email.toLowerCase().trim();

    // Don't add if it's the primary admin
    if (normalizedEmail === ADMIN_EMAIL.toLowerCase()) {
      throw new Error('Primary admin is already an admin');
    }

    // Get current admins
    const currentAdmins = await this.getAdminUsers();

    // Check if already exists
    if (currentAdmins.some(a => a.email.toLowerCase() === normalizedEmail)) {
      throw new Error('This email is already an admin');
    }

    // Add new admin
    const newAdmin: AdminUser = {
      email: normalizedEmail,
      addedAt: new Date().toISOString(),
      addedBy: addedByEmail,
    };

    const configRef = doc(db, ADMIN_CONFIG_COLLECTION, ADMIN_CONFIG_DOC);
    const updatedAdmins = [...currentAdmins, newAdmin];
    await setDoc(configRef, {
      additionalAdmins: updatedAdmins,
      adminEmails: updatedAdmins.map(a => a.email), // Flat array for Firestore rules
      updatedAt: new Date().toISOString(),
    }, { merge: true });

    // Clear cache
    this.adminEmailsCache = null;
  }

  /**
   * Remove an admin user (only primary admin can do this)
   */
  async removeAdminUser(email: string, removedByEmail: string): Promise<void> {
    if (!this.isPrimaryAdmin(removedByEmail)) {
      throw new Error('Only the primary admin can remove admins');
    }

    const normalizedEmail = email.toLowerCase().trim();

    // Can't remove primary admin
    if (normalizedEmail === ADMIN_EMAIL.toLowerCase()) {
      throw new Error('Cannot remove the primary admin');
    }

    // Get current admins
    const currentAdmins = await this.getAdminUsers();

    // Filter out the email to remove
    const updatedAdmins = currentAdmins.filter(
      a => a.email.toLowerCase() !== normalizedEmail
    );

    const configRef = doc(db, ADMIN_CONFIG_COLLECTION, ADMIN_CONFIG_DOC);
    await setDoc(configRef, {
      additionalAdmins: updatedAdmins,
      adminEmails: updatedAdmins.map(a => a.email), // Flat array for Firestore rules
      updatedAt: new Date().toISOString(),
    }, { merge: true });

    // Clear cache
    this.adminEmailsCache = null;
  }

  /**
   * Get all pilots (users) with their metrics
   * OPTIMIZED: Fetch all data once, then compute metrics in memory
   */
  async getAllUserMetrics(): Promise<UserMetrics[]> {
    // Fetch all data in parallel (single read per collection)
    const [pilotsSnapshot, profilesSnapshot, remindersSnapshot, healthSnapshot, financialSnapshot, insuranceSnapshot] = await Promise.all([
      getDocs(query(collection(db, PILOTS_COLLECTION), orderBy('createdAt', 'desc'))),
      getDocs(collection(db, PROFILES_COLLECTION)),
      getDocs(collection(db, REMINDERS_COLLECTION)),
      getDocs(collection(db, HEALTH_PROVIDERS_COLLECTION)),
      getDocs(collection(db, FINANCIAL_INSTITUTIONS_COLLECTION)),
      getDocs(collection(db, INSURANCE_POLICIES_COLLECTION)),
    ]);

    // Build lookup maps for O(1) access
    const profilesByOwner = new Map<string, any[]>();
    const remindersByProfile = new Map<string, number>();
    const healthByProfile = new Map<string, number>();
    const financialByProfile = new Map<string, number>();
    const insuranceByProfile = new Map<string, number>();

    // Index profiles by owner
    profilesSnapshot.forEach((doc) => {
      const data = doc.data();
      const ownerId = data.ownerId;
      if (!profilesByOwner.has(ownerId)) {
        profilesByOwner.set(ownerId, []);
      }
      profilesByOwner.get(ownerId)!.push({ id: doc.id, ...data });
    });

    // Count reminders by profile
    remindersSnapshot.forEach((doc) => {
      const profileId = doc.data().profileId;
      remindersByProfile.set(profileId, (remindersByProfile.get(profileId) || 0) + 1);
    });

    // Count health providers by profile
    healthSnapshot.forEach((doc) => {
      const profileId = doc.data().profileId;
      healthByProfile.set(profileId, (healthByProfile.get(profileId) || 0) + 1);
    });

    // Count financial institutions by profile
    financialSnapshot.forEach((doc) => {
      const profileId = doc.data().profileId;
      financialByProfile.set(profileId, (financialByProfile.get(profileId) || 0) + 1);
    });

    // Count insurance policies by profile
    insuranceSnapshot.forEach((doc) => {
      const profileId = doc.data().profileId;
      insuranceByProfile.set(profileId, (insuranceByProfile.get(profileId) || 0) + 1);
    });

    // Build user metrics
    const userMetrics: UserMetrics[] = [];

    for (const pilotDoc of pilotsSnapshot.docs) {
      const pilotData = pilotDoc.data();
      const pilotId = pilotDoc.id;

      const userProfiles = profilesByOwner.get(pilotId) || [];

      let profileCount = userProfiles.length;
      let totalAddresses = 0;
      let totalVehicles = 0;
      let totalMembers = 0;
      let rolesPilot = 0;
      let rolesCoPilot = 0;
      let rolesPassenger = 0;
      let totalReminders = 0;
      let totalHealthProviders = 0;
      let totalFinancialInstitutions = 0;
      let totalInsurancePolicies = 0;

      // Aggregate metrics from user's profiles
      for (const profile of userProfiles) {
        totalAddresses += profile.addresses?.length || 0;
        totalVehicles += profile.vehicles?.length || 0;
        totalMembers += profile.members?.length || 0;
        rolesPilot++;

        // Count Co-Pilots and Passengers in user's profiles
        if (profile.members && Array.isArray(profile.members)) {
          profile.members.forEach((member: any) => {
            if (member.userId === pilotId) return; // Skip owner
            if (member.role === 'editor') {
              rolesCoPilot++;
            } else if (member.role === 'viewer') {
              rolesPassenger++;
            }
          });
        }

        // Aggregate counts from subcollections
        totalReminders += remindersByProfile.get(profile.id) || 0;
        totalHealthProviders += healthByProfile.get(profile.id) || 0;
        totalFinancialInstitutions += financialByProfile.get(profile.id) || 0;
        totalInsurancePolicies += insuranceByProfile.get(profile.id) || 0;
      }

      // Format dates
      let createdAt = pilotData.createdAt;
      if (createdAt instanceof Timestamp) {
        createdAt = createdAt.toDate().toISOString();
      }

      let lastSignIn = pilotData.lastSignIn;
      if (lastSignIn instanceof Timestamp) {
        lastSignIn = lastSignIn.toDate().toISOString();
      }

      userMetrics.push({
        id: pilotId,
        email: pilotData.email,
        firstName: pilotData.firstName,
        lastName: pilotData.lastName,
        createdAt,
        lastSignIn,
        membershipPlan: (pilotData.membershipPlan as MembershipPlan) || 'free',
        accountStatus: (pilotData.accountStatus as AccountStatus) || 'active',
        disabledAt: pilotData.disabledAt,
        disabledBy: pilotData.disabledBy,
        disabledReason: pilotData.disabledReason,
        profileCount,
        totalAddresses,
        totalVehicles,
        totalMembers,
        totalReminders,
        totalHealthProviders,
        totalFinancialInstitutions,
        totalInsurancePolicies,
        rolesPilot,
        rolesCoPilot,
        rolesPassenger,
      });
    }

    return userMetrics;
  }

  /**
   * Get overall platform metrics
   * OPTIMIZED: Fetch all data once in parallel
   */
  async getOverallMetrics(): Promise<OverallMetrics> {
    // Fetch all data in parallel
    const [pilotsSnapshot, profilesSnapshot, remindersSnapshot, healthSnapshot, financialSnapshot, insuranceSnapshot] = await Promise.all([
      getDocs(collection(db, PILOTS_COLLECTION)),
      getDocs(collection(db, PROFILES_COLLECTION)),
      getDocs(collection(db, REMINDERS_COLLECTION)),
      getDocs(collection(db, HEALTH_PROVIDERS_COLLECTION)),
      getDocs(collection(db, FINANCIAL_INSTITUTIONS_COLLECTION)),
      getDocs(collection(db, INSURANCE_POLICIES_COLLECTION)),
    ]);

    const totalUsers = pilotsSnapshot.size;
    const totalProfiles = profilesSnapshot.size;
    const totalReminders = remindersSnapshot.size;
    const totalHealthProviders = healthSnapshot.size;
    const totalFinancialInstitutions = financialSnapshot.size;
    const totalInsurancePolicies = insuranceSnapshot.size;

    let totalAddresses = 0;
    let totalVehicles = 0;
    let totalMembers = 0;

    profilesSnapshot.forEach((doc) => {
      const data = doc.data();
      totalAddresses += data.addresses?.length || 0;
      totalVehicles += data.vehicles?.length || 0;
      totalMembers += data.members?.length || 0;
    });

    // Calculate time-based metrics
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    let usersThisWeek = 0;
    let usersThisMonth = 0;
    let activeUsersLast7Days = 0;

    pilotsSnapshot.forEach((doc) => {
      const data = doc.data();

      // Check createdAt
      let createdAt = data.createdAt;
      if (createdAt instanceof Timestamp) {
        createdAt = createdAt.toDate();
      } else if (typeof createdAt === 'string') {
        createdAt = new Date(createdAt);
      }

      if (createdAt && createdAt >= oneWeekAgo) {
        usersThisWeek++;
      }
      if (createdAt && createdAt >= oneMonthAgo) {
        usersThisMonth++;
      }

      // Check lastSignIn for active users
      let lastSignIn = data.lastSignIn;
      if (lastSignIn instanceof Timestamp) {
        lastSignIn = lastSignIn.toDate();
      } else if (typeof lastSignIn === 'string') {
        lastSignIn = new Date(lastSignIn);
      }

      if (lastSignIn && lastSignIn >= oneWeekAgo) {
        activeUsersLast7Days++;
      }
    });

    // Calculate averages
    const avgAddressesPerUser = totalUsers > 0 ? Math.round((totalAddresses / totalUsers) * 10) / 10 : 0;
    const avgVehiclesPerUser = totalUsers > 0 ? Math.round((totalVehicles / totalUsers) * 10) / 10 : 0;
    const avgRemindersPerUser = totalUsers > 0 ? Math.round((totalReminders / totalUsers) * 10) / 10 : 0;

    // Calculate engagement metrics
    // 1. Users who added first item within 24h of signup
    let usersWithFirstItemIn24h = 0;

    // 2. Users who returned after 7 days (signed up more than 7 days ago and have been active since)
    let usersReturnedAfter7Days = 0;
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    // 3. Users with at least one reminder
    const usersWithReminders = new Set<string>();

    // Map profile IDs to owner IDs for engagement calculation
    const profileToOwnerMap = new Map<string, string>();
    const profileCreatedAtMap = new Map<string, Date>();

    profilesSnapshot.forEach((doc) => {
      const data = doc.data();
      profileToOwnerMap.set(doc.id, data.ownerId);

      let profileCreatedAt = data.createdAt;
      if (profileCreatedAt instanceof Timestamp) {
        profileCreatedAt = profileCreatedAt.toDate();
      } else if (typeof profileCreatedAt === 'string') {
        profileCreatedAt = new Date(profileCreatedAt);
      }
      if (profileCreatedAt) {
        profileCreatedAtMap.set(doc.id, profileCreatedAt);
      }
    });

    // Check reminders for user engagement
    remindersSnapshot.forEach((doc) => {
      const data = doc.data();
      const ownerId = profileToOwnerMap.get(data.profileId);
      if (ownerId) {
        usersWithReminders.add(ownerId);
      }
    });

    // Calculate per-user engagement
    pilotsSnapshot.forEach((doc) => {
      const data = doc.data();
      const pilotId = doc.id;

      let createdAt = data.createdAt;
      if (createdAt instanceof Timestamp) {
        createdAt = createdAt.toDate();
      } else if (typeof createdAt === 'string') {
        createdAt = new Date(createdAt);
      }

      let lastSignIn = data.lastSignIn;
      if (lastSignIn instanceof Timestamp) {
        lastSignIn = lastSignIn.toDate();
      } else if (typeof lastSignIn === 'string') {
        lastSignIn = new Date(lastSignIn);
      }

      // Check if user added first item within 24h
      // We consider "first item" as any address, vehicle, reminder, etc.
      if (createdAt) {
        const twentyFourHoursAfterSignup = new Date(createdAt.getTime() + 24 * 60 * 60 * 1000);

        // Check if user has any profile with data
        let hasFirstItemIn24h = false;
        profilesSnapshot.forEach((profileDoc) => {
          const profileData = profileDoc.data();
          if (profileData.ownerId === pilotId) {
            // Check if profile has any data (addresses, vehicles)
            const hasAddresses = profileData.addresses?.length > 0;
            const hasVehicles = profileData.vehicles?.length > 0;

            // For simplicity, we check if profile was created within 24h and has data
            const profileCreated = profileCreatedAtMap.get(profileDoc.id);
            if (profileCreated && profileCreated <= twentyFourHoursAfterSignup) {
              if (hasAddresses || hasVehicles) {
                hasFirstItemIn24h = true;
              }
            }
          }
        });

        if (hasFirstItemIn24h) {
          usersWithFirstItemIn24h++;
        }

        // Check if user returned after 7 days
        // User must have signed up more than 7 days ago AND have signed in within the last 7 days
        if (createdAt < sevenDaysAgo && lastSignIn && lastSignIn >= sevenDaysAgo) {
          usersReturnedAfter7Days++;
        }
      }
    });

    // Calculate users older than 7 days (for retention percentage calculation)
    let usersOlderThan7Days = 0;
    pilotsSnapshot.forEach((doc) => {
      const data = doc.data();
      let createdAt = data.createdAt;
      if (createdAt instanceof Timestamp) {
        createdAt = createdAt.toDate();
      } else if (typeof createdAt === 'string') {
        createdAt = new Date(createdAt);
      }
      if (createdAt && createdAt < sevenDaysAgo) {
        usersOlderThan7Days++;
      }
    });

    // 4. Share link -> signup conversion
    // We track this by checking if users have a pendingBundleImport field or came from share link
    // For now, we'll count users who have imported at least one shared bundle
    let shareLinkSignups = 0;
    // Note: This would require tracking in the pilots collection when users sign up from share links
    // For now, we'll estimate based on profiles that have imported contacts

    const usersWithAtLeastOneReminder = usersWithReminders.size;
    const usersWithFirstItemIn24hPercent = totalUsers > 0 ? Math.round((usersWithFirstItemIn24h / totalUsers) * 100) : 0;
    const usersReturnedAfter7DaysPercent = usersOlderThan7Days > 0 ? Math.round((usersReturnedAfter7Days / usersOlderThan7Days) * 100) : 0;
    const usersWithAtLeastOneReminderPercent = totalUsers > 0 ? Math.round((usersWithAtLeastOneReminder / totalUsers) * 100) : 0;
    const shareLinkConversionPercent = 0; // Would need additional tracking

    const result = {
      totalUsers,
      totalProfiles,
      totalAddresses,
      totalVehicles,
      totalMembers,
      totalReminders,
      totalHealthProviders,
      totalFinancialInstitutions,
      totalInsurancePolicies,
      usersThisWeek,
      usersThisMonth,
      activeUsersLast7Days,
      avgAddressesPerUser,
      avgVehiclesPerUser,
      avgRemindersPerUser,
      // Engagement metrics
      usersWithFirstItemIn24h,
      usersWithFirstItemIn24hPercent,
      usersReturnedAfter7Days,
      usersReturnedAfter7DaysPercent,
      usersWithAtLeastOneReminder,
      usersWithAtLeastOneReminderPercent,
      shareLinkSignups,
      shareLinkConversionPercent,
    };

    return result;
  }

  /**
   * Get signup trends (daily counts for last 30 days)
   */
  async getSignupTrends(): Promise<SignupTrend[]> {
    const pilotsRef = collection(db, PILOTS_COLLECTION);
    const pilotsSnapshot = await getDocs(pilotsRef);

    // Initialize date map for last 30 days
    const trends: Map<string, number> = new Map();
    const now = new Date();

    for (let i = 29; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const dateKey = date.toISOString().split('T')[0];
      trends.set(dateKey, 0);
    }

    // Count signups per day
    pilotsSnapshot.forEach((doc) => {
      const data = doc.data();
      let createdAt = data.createdAt;

      if (createdAt instanceof Timestamp) {
        createdAt = createdAt.toDate();
      } else if (typeof createdAt === 'string') {
        createdAt = new Date(createdAt);
      }

      if (createdAt) {
        const dateKey = createdAt.toISOString().split('T')[0];
        if (trends.has(dateKey)) {
          trends.set(dateKey, (trends.get(dateKey) || 0) + 1);
        }
      }
    });

    return Array.from(trends.entries()).map(([date, count]) => ({
      date,
      count,
    }));
  }

  /**
   * Get category breakdown (how many users use each feature)
   */
  async getCategoryBreakdown(): Promise<CategoryBreakdown[]> {
    const pilotsSnapshot = await getDocs(collection(db, PILOTS_COLLECTION));
    const totalUsers = pilotsSnapshot.size;

    if (totalUsers === 0) {
      return [];
    }

    const profilesSnapshot = await getDocs(collection(db, PROFILES_COLLECTION));
    const remindersSnapshot = await getDocs(collection(db, REMINDERS_COLLECTION));
    const healthSnapshot = await getDocs(collection(db, HEALTH_PROVIDERS_COLLECTION));
    const financialSnapshot = await getDocs(collection(db, FINANCIAL_INSTITUTIONS_COLLECTION));
    const insuranceSnapshot = await getDocs(collection(db, INSURANCE_POLICIES_COLLECTION));

    // Count users who have at least one of each category
    const usersWithAddresses = new Set<string>();
    const usersWithVehicles = new Set<string>();
    const usersWithReminders = new Set<string>();
    const usersWithHealth = new Set<string>();
    const usersWithFinancial = new Set<string>();
    const usersWithInsurance = new Set<string>();
    const usersWithMembers = new Set<string>();

    // Map profile IDs to owner IDs
    const profileToOwner = new Map<string, string>();
    profilesSnapshot.forEach((doc) => {
      const data = doc.data();
      profileToOwner.set(doc.id, data.ownerId);

      if (data.addresses?.length > 0) {
        usersWithAddresses.add(data.ownerId);
      }
      if (data.vehicles?.length > 0) {
        usersWithVehicles.add(data.ownerId);
      }
      if (data.members?.length > 1) { // More than just owner
        usersWithMembers.add(data.ownerId);
      }
    });

    remindersSnapshot.forEach((doc) => {
      const data = doc.data();
      const ownerId = profileToOwner.get(data.profileId);
      if (ownerId) {
        usersWithReminders.add(ownerId);
      }
    });

    healthSnapshot.forEach((doc) => {
      const data = doc.data();
      const ownerId = profileToOwner.get(data.profileId);
      if (ownerId) {
        usersWithHealth.add(ownerId);
      }
    });

    financialSnapshot.forEach((doc) => {
      const data = doc.data();
      const ownerId = profileToOwner.get(data.profileId);
      if (ownerId) {
        usersWithFinancial.add(ownerId);
      }
    });

    insuranceSnapshot.forEach((doc) => {
      const data = doc.data();
      const ownerId = profileToOwner.get(data.profileId);
      if (ownerId) {
        usersWithInsurance.add(ownerId);
      }
    });

    const categories = [
      { category: 'Addresses', count: usersWithAddresses.size },
      { category: 'Vehicles', count: usersWithVehicles.size },
      { category: 'Reminders', count: usersWithReminders.size },
      { category: 'Healthcare', count: usersWithHealth.size },
      { category: 'Finance', count: usersWithFinancial.size },
      { category: 'Insurance', count: usersWithInsurance.size },
      { category: 'Family Members', count: usersWithMembers.size },
    ];

    return categories.map((cat) => ({
      ...cat,
      percentage: Math.round((cat.count / totalUsers) * 100),
    }));
  }

  /**
   * Switch a user's membership plan (admin only)
   */
  async switchUserMembership(
    userId: string,
    userEmail: string,
    newPlan: MembershipPlan,
    adminEmail: string,
    note?: string
  ): Promise<void> {
    // Verify admin
    const isAuthorized = await this.isAuthorizedAdminAsync(adminEmail);
    if (!isAuthorized) {
      throw new Error('Only admins can change user memberships');
    }

    // Get current plan
    const pilotRef = doc(db, PILOTS_COLLECTION, userId);
    const pilotSnap = await getDoc(pilotRef);

    if (!pilotSnap.exists()) {
      throw new Error('User not found');
    }

    const currentPlan = pilotSnap.data().membershipPlan || 'free';

    // Update pilot document with new plan
    await updateDoc(pilotRef, {
      membershipPlan: newPlan,
      membershipUpdatedAt: new Date().toISOString(),
      membershipUpdatedBy: adminEmail,
    });

    // Handle downgrade from Pro to Free: Convert Co-Pilots to Passengers
    let coPilotsConverted = 0;
    if (currentPlan === 'pro' && newPlan === 'free') {
      coPilotsConverted = await this.convertCoPilotsToPassengers(userId);
    }

    // Log the action
    await this.logAdminAction({
      action: 'switch_membership',
      targetUserId: userId,
      targetUserEmail: userEmail,
      adminEmail,
      oldValue: currentPlan,
      newValue: newPlan,
      note: coPilotsConverted > 0
        ? `${note ? note + '. ' : ''}Converted ${coPilotsConverted} Editor(s) to Viewer(s).`
        : note,
    });
  }

  /**
   * Convert all Co-Pilots to Passengers for a user's profiles
   * Called when a user downgrades from Pro to Free (Free plan has 0 Co-Pilots allowed)
   * @returns Number of Co-Pilots converted
   */
  private async convertCoPilotsToPassengers(ownerId: string): Promise<number> {
    try {
      // Get all profiles owned by this user
      const profilesRef = collection(db, PROFILES_COLLECTION);
      const q = query(profilesRef, where('ownerId', '==', ownerId));
      const querySnapshot = await getDocs(q);

      let totalConverted = 0;

      for (const profileDoc of querySnapshot.docs) {
        const profileData = profileDoc.data();
        const members: FamilyMember[] = profileData.members || [];

        // Find Co-Pilots (editors) and convert to Passengers (viewers)
        let hasChanges = false;
        const updatedMembers = members.map(member => {
          if (member.role === 'editor') {
            hasChanges = true;
            totalConverted++;
            return {
              ...member,
              role: 'viewer' as const,
              // Track the downgrade for reference
              downgradedFromEditor: true,
              downgradedAt: new Date().toISOString(),
            };
          }
          return member;
        });

        // Update the profile if there were changes
        if (hasChanges) {
          await ProfileService.updateProfile(profileDoc.id, { members: updatedMembers });
        }
      }

      return totalConverted;
    } catch (error) {
      console.error('Error converting Co-Pilots to Passengers:', error);
      return 0;
    }
  }

  /**
   * Disable a user account (admin only)
   */
  async disableAccount(
    userId: string,
    userEmail: string,
    adminEmail: string,
    reason?: string
  ): Promise<void> {
    // Verify admin
    const isAuthorized = await this.isAuthorizedAdminAsync(adminEmail);
    if (!isAuthorized) {
      throw new Error('Only admins can disable accounts');
    }

    // Prevent disabling admin accounts
    const isTargetAdmin = await this.isAuthorizedAdminAsync(userEmail);
    if (isTargetAdmin) {
      throw new Error('Cannot disable admin accounts');
    }

    const pilotRef = doc(db, PILOTS_COLLECTION, userId);
    const pilotSnap = await getDoc(pilotRef);

    if (!pilotSnap.exists()) {
      throw new Error('User not found');
    }

    // Update pilot document
    await updateDoc(pilotRef, {
      accountStatus: 'disabled',
      disabledAt: new Date().toISOString(),
      disabledBy: adminEmail,
      disabledReason: reason || '',
    });

    // Log the action
    await this.logAdminAction({
      action: 'disable_account',
      targetUserId: userId,
      targetUserEmail: userEmail,
      adminEmail,
      oldValue: 'active',
      newValue: 'disabled',
      note: reason,
    });
  }

  /**
   * Enable a disabled user account (admin only)
   */
  async enableAccount(
    userId: string,
    userEmail: string,
    adminEmail: string
  ): Promise<void> {
    // Verify admin
    const isAuthorized = await this.isAuthorizedAdminAsync(adminEmail);
    if (!isAuthorized) {
      throw new Error('Only admins can enable accounts');
    }

    const pilotRef = doc(db, PILOTS_COLLECTION, userId);
    const pilotSnap = await getDoc(pilotRef);

    if (!pilotSnap.exists()) {
      throw new Error('User not found');
    }

    // Update pilot document
    await updateDoc(pilotRef, {
      accountStatus: 'active',
      disabledAt: null,
      disabledBy: null,
      disabledReason: null,
    });

    // Log the action
    await this.logAdminAction({
      action: 'enable_account',
      targetUserId: userId,
      targetUserEmail: userEmail,
      adminEmail,
      oldValue: 'disabled',
      newValue: 'active',
    });
  }

  /**
   * Hard delete a user and all their data (admin only)
   * Permanently removes: pilot doc, profiles, subcollections, storage files, families doc
   */
  async hardDeleteUser(
    userId: string,
    userEmail: string,
    adminEmail: string
  ): Promise<void> {
    // Verify admin
    const isAuthorized = await this.isAuthorizedAdminAsync(adminEmail);
    if (!isAuthorized) {
      throw new Error('Only admins can delete accounts');
    }

    // Prevent deleting admin accounts
    const isTargetAdmin = await this.isAuthorizedAdminAsync(userEmail);
    if (isTargetAdmin) {
      throw new Error('Cannot delete admin accounts');
    }

    const pilotRef = doc(db, PILOTS_COLLECTION, userId);
    const pilotSnap = await getDoc(pilotRef);

    if (!pilotSnap.exists()) {
      throw new Error('User not found');
    }

    // Log BEFORE deletion (pilot doc will be gone after)
    await this.logAdminAction({
      action: 'hard_delete_user',
      targetUserId: userId,
      targetUserEmail: userEmail,
      adminEmail,
      note: 'Permanent deletion of all user data',
    });

    // 1. Delete Firebase Storage files
    try {
      const userStorageRef = ref(storage, `users/${userId}`);
      const listResult = await listAll(userStorageRef);
      const storageDeletes: Promise<void>[] = listResult.items.map(item =>
        deleteObject(item).catch(err => console.warn(`Failed to delete ${item.fullPath}:`, err))
      );
      for (const folder of listResult.prefixes) {
        const folderList = await listAll(folder);
        folderList.items.forEach(item => {
          storageDeletes.push(deleteObject(item).catch(err => console.warn(`Failed to delete ${item.fullPath}:`, err)));
        });
        for (const subFolder of folderList.prefixes) {
          const subList = await listAll(subFolder);
          subList.items.forEach(item => {
            storageDeletes.push(deleteObject(item).catch(err => console.warn(`Failed to delete ${item.fullPath}:`, err)));
          });
        }
      }
      await Promise.all(storageDeletes);
    } catch {
      console.warn('Storage cleanup failed (may not exist)');
    }

    // 2. Delete profiles and their subcollections
    const profilesRef = collection(db, PROFILES_COLLECTION);
    const profileQuery = query(profilesRef, where('ownerId', '==', userId));
    const profileSnapshot = await getDocs(profileQuery);

    const docDeletes: Promise<void>[] = [];

    for (const profileDoc of profileSnapshot.docs) {
      const profileId = profileDoc.id;
      const subcollections = ['health', 'financial', 'insurance', 'shareBundles'];

      for (const sub of subcollections) {
        const subRef = collection(db, PROFILES_COLLECTION, profileId, sub);
        const subSnap = await getDocs(subRef);
        subSnap.docs.forEach(subDoc => {
          docDeletes.push(deleteDoc(doc(db, PROFILES_COLLECTION, profileId, sub, subDoc.id)));
        });
      }

      // Delete profile document
      docDeletes.push(deleteDoc(doc(db, PROFILES_COLLECTION, profileId)));
    }

    // 3. Delete families document
    docDeletes.push(deleteDoc(doc(db, 'families', userId)));

    // 4. Delete pilot document
    docDeletes.push(deleteDoc(pilotRef));

    await Promise.all(docDeletes);
  }

  /**
   * Log an admin action for audit trail
   */
  async logAdminAction(log: Omit<AdminAuditLog, 'id' | 'timestamp'>): Promise<void> {
    try {
      const auditRef = collection(db, ADMIN_AUDIT_COLLECTION);
      // Filter out undefined fields to avoid Firestore errors
      const logData: any = {
        action: log.action,
        targetUserId: log.targetUserId,
        targetUserEmail: log.targetUserEmail,
        adminEmail: log.adminEmail,
        timestamp: new Date().toISOString(),
      };

      // Only add optional fields if they're defined
      if (log.oldValue !== undefined) logData.oldValue = log.oldValue;
      if (log.newValue !== undefined) logData.newValue = log.newValue;
      if (log.note !== undefined) logData.note = log.note;

      await addDoc(auditRef, logData);
    } catch (error) {
      console.error('Error logging admin action:', error);
      // Don't throw - audit logging shouldn't block the main action
    }
  }

  /**
   * Get audit logs (most recent first)
   */
  async getAuditLogs(limit: number = 50): Promise<AdminAuditLog[]> {
    try {
      const auditRef = collection(db, ADMIN_AUDIT_COLLECTION);
      const auditSnapshot = await getDocs(query(auditRef, orderBy('timestamp', 'desc')));

      const logs: AdminAuditLog[] = [];
      let count = 0;

      auditSnapshot.forEach((docSnap) => {
        if (count < limit) {
          const data = docSnap.data();
          logs.push({
            id: docSnap.id,
            action: data.action,
            targetUserId: data.targetUserId,
            targetUserEmail: data.targetUserEmail,
            adminEmail: data.adminEmail,
            oldValue: data.oldValue,
            newValue: data.newValue,
            note: data.note,
            timestamp: data.timestamp,
          });
          count++;
        }
      });

      return logs;
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return [];
    }
  }
}

export const adminMetricsService = new AdminMetricsService();
